import React from 'react';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { sortAllocations } from '../utils/sortAllocations';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';
import AvatarWithName from 'components/new/AvatarWithName';
import { styled } from '@mui/material';
import { Column } from 'further-ui/components/Layout';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

interface FeeAllocation {
  investorName: string;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
  isNewTransferee?: boolean;
}

interface FeeCardProps {
  fee: {
    feeKey: string;
    originalFeeId: string;
    lpFeeTemplateFeeName: string;
    accrualDate: CalendarDay;
    isApproved: boolean;
    originalAllocations: Array<FeeAllocation>;
    newAllocations: Array<FeeAllocation>;
  };
  baseCurrency: string;
}

const FeeCard: React.FC<FeeCardProps> = ({ fee, baseCurrency }) => {
  const sortedOriginalAllocations = sortAllocations(fee.originalAllocations);
  const sortedNewAllocations = sortAllocations(fee.newAllocations);

  const originalRows = sortedOriginalAllocations.map((allocation, idx) => ({
    id: `original-${idx}`,
    investorName: allocation.investorName,
    feeExTaxLcy: allocation.feeExTaxLcy,
    salesTaxAmountLcy: allocation.salesTaxAmountLcy,
    feeIncTaxLcy: allocation.feeIncTaxLcy,
  }));

  const newRows = sortedNewAllocations.map((allocation, idx) => ({
    id: `new-${idx}`,
    investorName: allocation.investorName,
    feeExTaxLcy: allocation.feeExTaxLcy,
    salesTaxAmountLcy: allocation.salesTaxAmountLcy,
    feeIncTaxLcy: allocation.feeIncTaxLcy,
  }));

  type Row = (typeof originalRows)[number];

  const columns: Array<TableColumn<Row>> = [
    {
      key: 'investorName',
      label: 'Limited partner',
      render: (row) => <AvatarWithName name={row.investorName} />,
    },
    {
      key: 'feeExTaxLcy',
      label: 'Fee',
      render: (row) =>
        numberToCurrencyString(row.feeExTaxLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'salesTaxAmountLcy',
      label: 'Sales tax',
      render: (row) =>
        numberToCurrencyString(row.salesTaxAmountLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'feeIncTaxLcy',
      label: 'Total charge',
      render: (row) =>
        numberToCurrencyString(row.feeIncTaxLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
  ];

  return (
    <ContentColumn>
      <TabsPanel
        defaultTab="after"
        tabs={[
          {
            key: 'after',
            label: 'New allocation',
            content: (
              <Table
                showTopBorder
                showHeaderBorder
                columns={columns}
                rows={newRows}
                scrollable={true}
              />
            ),
          },
          {
            key: 'before',
            label: 'Original allocation',
            content: (
              <Table
                showTopBorder
                showHeaderBorder
                columns={columns}
                rows={originalRows}
                scrollable={true}
              />
            ),
          },
        ]}
      />
    </ContentColumn>
  );
};

export default FeeCard;
